import { useEffect, useRef, useState, useCallback } from "react";
import api, { errorMessage } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "../components/ui/select";
import { Copy, Heart, Trash2, Lock, Pencil, X, Plus, FolderPlus, ListPlus, Check, AlertTriangle, FolderOpen, Search, FileDown, Eye, ImagePlus, ImageIcon, Sparkles, Film, Cloud, Wand2, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";

const STATUS_UI = {
  works:     { color: "text-emerald-400", bg: "bg-emerald-500/15", label: "works",     Icon: Check },
  needs_fix: { color: "text-amber-400",   bg: "bg-amber-500/15",   label: "needs_fix", Icon: AlertTriangle },
};

/** Fetches a prompt thumbnail (private, requires auth) and shows it as blob URL. */
function PromptThumb({ promptId, version, size = 48, className = "" }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    (async () => {
      try {
        const res = await api.get(`/prompts/${promptId}/thumbnail`, { responseType: "blob" });
        if (cancelled) return;
        objectUrl = window.URL.createObjectURL(res.data);
        setUrl(objectUrl);
      } catch { if (!cancelled) setUrl(null); }
    })();
    return () => { cancelled = true; if (objectUrl) window.URL.revokeObjectURL(objectUrl); };
  }, [promptId, version]);
  if (!url) return null;
  return (
    <img
      src={url}
      alt=""
      loading="lazy"
      data-testid={`thumb-img-${promptId}`}
      style={{ width: size, height: size }}
      className={`object-cover rounded shrink-0 border border-white/10 ${className}`}
    />
  );
}

export default function History() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [lists, setLists] = useState([]);
  const [tab, setTab] = useState("all");           // all | favorites | works | needs_fix
  const [activeListId, setActiveListId] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [editingTitleId, setEditingTitleId] = useState(null);
  const [titleDraft, setTitleDraft] = useState("");
  const [showManual, setShowManual] = useState(false);
  const [showNewList, setShowNewList] = useState(false);
  const [newListName, setNewListName] = useState("");
  const [addToListMenu, setAddToListMenu] = useState(null); // prompt_id when menu open
  const addToListMenuRef = useRef(null);
  const [bulkAddMenu, setBulkAddMenu] = useState(false);
  const [manualForm, setManualForm] = useState({ title: "", generated_prompt: "", model_name: "", output_type: "image" });
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [listNamesPopover, setListNamesPopover] = useState(null); // prompt_id when popover open
  const [previewPrompt, setPreviewPrompt] = useState(null);
  const [thumbUploadingFor, setThumbUploadingFor] = useState(null);
  const thumbFileInputRef = useRef(null);
  const thumbTargetIdRef = useRef(null);
  const hasPlan = !!user; // library UI open to all logged-in users; backend enforces per-plan limits
  const isFreeTier = user?.plan === "pending";
  const canExportPdf = user && ["basic", "plus", "pro"].includes(user.plan);
  const [usage, setUsage] = useState({ manual_used: 0, manual_limit: 5, lists_used: 0, lists_limit: 1 });
  const [capModal, setCapModal] = useState(null); // { type: 'manual'|'list', limit, used }

  // Intercept limit-reached errors from backend and open the upgrade modal instead of a toast
  const handleCapError = (err, fallbackToastKey) => {
    const code = err?.response?.data?.detail?.code;
    if (code === "manual_limit_reached") {
      setCapModal({ type: "manual", limit: usage.manual_limit, used: usage.manual_used });
      return true;
    }
    if (code === "list_limit_reached") {
      setCapModal({ type: "list", limit: usage.lists_limit, used: usage.lists_used });
      return true;
    }
    toast.error(errorMessage(err, t) || (fallbackToastKey ? t(fallbackToastKey) : "Error"));
    return false;
  };

  const load = useCallback(async () => {
    const params = {};
    if (tab === "favorites") params.favorites_only = true;
    if (tab === "works" || tab === "needs_fix") params.status = tab;
    if (activeListId) params.list_id = activeListId;
    if (debouncedQuery) params.q = debouncedQuery;
    const { data } = await api.get("/prompts", { params });
    setItems(data);
    setSelected(new Set());
  }, [tab, activeListId, debouncedQuery]);

  const loadLists = useCallback(async () => {
    if (!hasPlan) return;
    try { const { data } = await api.get("/prompts/lists"); setLists(data); } catch { /* noop */ void 0; }
  }, [hasPlan]);

  const loadUsage = useCallback(async () => {
    if (!hasPlan) return;
    try { const { data } = await api.get("/prompts/library-usage"); setUsage(data); } catch { /* noop */ void 0; }
  }, [hasPlan]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadLists(); }, [loadLists]);
  useEffect(() => { loadUsage(); }, [loadUsage]);
  // Debounce search input (300ms) so we don't spam the API on each keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(searchQuery.trim()), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Close the per-prompt "manage lists" popover when clicking outside or pressing Escape.
  useEffect(() => {
    if (!addToListMenu) return;
    const handlePointer = (e) => {
      if (addToListMenuRef.current && !addToListMenuRef.current.contains(e.target)) {
        setAddToListMenu(null);
      }
    };
    const handleKey = (e) => { if (e.key === "Escape") setAddToListMenu(null); };
    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("keydown", handleKey);
    };
  }, [addToListMenu]);

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success(t("copied")); };
  const del = async (id) => { await api.delete(`/prompts/${id}`); toast.success(t("deleted")); load(); loadLists(); };
  const toggleFav = async (id) => {
    try { await api.post(`/prompts/${id}/favorite`); load(); }
    catch (err) { toast.error(errorMessage(err, t)); }
  };

  const beginEditTitle = (p) => { setEditingTitleId(p.prompt_id); setTitleDraft(p.title || ""); };
  const saveTitle = async (p) => {
    const newTitle = titleDraft.trim();
    setEditingTitleId(null);
    if (newTitle === (p.title || "")) return;
    try { await api.patch(`/prompts/${p.prompt_id}`, { title: newTitle }); load(); }
    catch (err) { toast.error(errorMessage(err, t)); }
  };
  const cycleStatus = async (p) => {
    const next = p.status === "works" ? "needs_fix" : "works";
    try { await api.patch(`/prompts/${p.prompt_id}`, { status: next }); load(); }
    catch (err) { toast.error(errorMessage(err, t)); }
  };

  const createList = async () => {
    const name = newListName.trim();
    if (!name) return;
    try {
      await api.post("/prompts/lists", { name });
      setNewListName(""); setShowNewList(false);
      toast.success(t("list_created") || "List created");
      loadLists();
      loadUsage();
    } catch (err) { handleCapError(err); }
  };
  const deleteList = async (list_id) => {
    if (!window.confirm(t("delete_list_confirm") || "Delete this list? (prompts stay in your library)")) return;
    try {
      await api.delete(`/prompts/lists/${list_id}`);
      if (activeListId === list_id) setActiveListId("");
      loadLists();
      loadUsage();
    } catch (err) { toast.error(errorMessage(err, t)); }
  };
  const addPromptToList = async (prompt_id, list_id) => {
    try {
      await api.post(`/prompts/lists/${list_id}/add`, { prompt_id });
      toast.success(t("added_to_list") || "Added to list");
      setAddToListMenu(null);
      loadLists();
      load(); // refresh prompt list so list_names badge updates immediately
    } catch (err) { toast.error(errorMessage(err, t)); }
  };

  const togglePromptInList = async (prompt_id, list_id) => {
    const inList = !!lists.find((L) => L.list_id === list_id)?.prompt_ids?.includes(prompt_id);
    try {
      if (inList) {
        await api.post(`/prompts/lists/${list_id}/remove`, { prompt_id });
        toast.success(t("removed_from_list") || "Removed from list");
      } else {
        await api.post(`/prompts/lists/${list_id}/add`, { prompt_id });
        toast.success(t("added_to_list") || "Added to list");
      }
      loadLists();
      load();
    } catch (err) { toast.error(errorMessage(err, t)); }
  };

  const submitManual = async () => {
    const text = manualForm.generated_prompt.trim();
    if (text.length < 5) { toast.error(t("prompt_too_short") || "Prompt too short"); return; }
    try {
      await api.post("/prompts/manual", { ...manualForm, generated_prompt: text });
      toast.success(t("manual_added") || "Prompt saved to library");
      setShowManual(false);
      setManualForm({ title: "", generated_prompt: "", model_name: "", output_type: "image" });
      load();
      loadUsage();
    } catch (err) { handleCapError(err); }
  };

  const toggleSelect = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAll = () => setSelected(new Set(items.map((p) => p.prompt_id)));
  const clearSelection = () => setSelected(new Set());
  const bulkDelete = async () => {
    if (selected.size === 0 || !window.confirm(t("bulk_delete_confirm") || `Delete ${selected.size} prompt(s)?`)) return;
    setBulkBusy(true);
    try { await api.post("/prompts/bulk/delete", { prompt_ids: Array.from(selected) }); toast.success(t("deleted")); load(); loadLists(); }
    catch (err) { toast.error(errorMessage(err, t)); }
    finally { setBulkBusy(false); }
  };
  const bulkExport = async () => {
    if (!canExportPdf) { toast.error(t("export_paid_only") || "Available on paid plans"); return; }
    if (selected.size === 0) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/prompts/bulk/export", { prompt_ids: Array.from(selected) }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `vixelforge-prompts-${Date.now()}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(errorMessage(err, t)); }
    finally { setBulkBusy(false); }
  };

  const bulkZip = async () => {
    if (!canExportPdf) { toast.error(t("export_paid_only") || "Available on paid plans"); return; }
    if (selected.size === 0) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/prompts/bulk/zip", { prompt_ids: Array.from(selected) }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data], { type: "application/zip" }));
      const a = document.createElement("a"); a.href = url; a.download = `vixelforge-prompts-${Date.now()}.zip`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(errorMessage(err, t)); }
    finally { setBulkBusy(false); }
  };

  const singleExport = async (p) => {
    if (!canExportPdf) { toast.error(t("export_paid_only") || "Available on paid plans"); return; }
    try {
      const { data } = await api.get(`/prompts/${p.prompt_id}/export`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const safeTitle = (p.title || "prompt").replace(/[^\w\-. ]+/g, "").trim().slice(0, 40) || "prompt";
      const a = document.createElement("a"); a.href = url; a.download = `vixelforge-${safeTitle}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(errorMessage(err, t)); }
  };

  // Thumbnail: same "paid plans" gating as PDF export
  const canUploadThumb = user && ["basic", "plus", "pro"].includes(user.plan);

  const openThumbPicker = (promptId) => {
    if (!canUploadThumb) { toast.error(t("thumbnail_paid_only") || "Available on paid plans"); return; }
    thumbTargetIdRef.current = promptId;
    thumbFileInputRef.current?.click();
  };

  const onThumbFileSelected = async (e) => {
    const file = e.target.files?.[0];
    const promptId = thumbTargetIdRef.current;
    e.target.value = ""; // allow re-selecting same file later
    if (!file || !promptId) return;
    if (file.size > 5 * 1024 * 1024) { toast.error(t("thumb_too_large") || "Max 5 MB"); return; }
    setThumbUploadingFor(promptId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/prompts/${promptId}/thumbnail`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("thumb_uploaded") || "Image saved");
      await load();
      // Also update the currently open preview dialog if it matches
      setPreviewPrompt((prev) => prev && prev.prompt_id === promptId
        ? { ...prev, has_thumbnail: true, thumbnail_updated_at: new Date().toISOString() }
        : prev);
    } catch (err) { toast.error(errorMessage(err, t)); }
    finally { setThumbUploadingFor(null); }
  };

  const deleteThumb = async (promptId) => {
    try {
      await api.delete(`/prompts/${promptId}/thumbnail`);
      toast.success(t("thumb_removed") || "Image removed");
      await load();
      setPreviewPrompt((prev) => prev && prev.prompt_id === promptId
        ? { ...prev, has_thumbnail: false }
        : prev);
    } catch (err) { toast.error(errorMessage(err, t)); }
  };

  const bulkAddToList = async (list_id) => {
    if (selected.size === 0 || !list_id) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post(`/prompts/lists/${list_id}/bulk-add`, { prompt_ids: Array.from(selected) });
      toast.success(`${data.added || 0} ${t("added_to_list") || "added to list"}`);
      setBulkAddMenu(false);
      clearSelection();
      loadLists();
      load(); // refresh prompt list so list_names badge updates immediately
    } catch (err) { toast.error(errorMessage(err, t)); }
    finally { setBulkBusy(false); }
  };

  const bulkCreateAndAdd = async () => {
    const name = (newListName || "").trim();
    if (!name || selected.size === 0) return;
    setBulkBusy(true);
    try {
      const { data: created } = await api.post("/prompts/lists", { name });
      const list_id = created.list_id;
      const { data } = await api.post(`/prompts/lists/${list_id}/bulk-add`, { prompt_ids: Array.from(selected) });
      toast.success(`${data.added || 0} ${t("added_to_list") || "added to list"}`);
      setNewListName("");
      setBulkAddMenu(false);
      clearSelection();
      loadLists();
      load(); // refresh prompt list so list_names badge updates immediately
    } catch (err) { toast.error(errorMessage(err, t)); }
    finally { setBulkBusy(false); }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl">
      {/* Hidden input driving all thumbnail uploads across rows and the preview dialog */}
      <input
        ref={thumbFileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
        style={{ display: "none" }}
        onChange={onThumbFileSelected}
        data-testid="thumb-file-input"
      />
      <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-orange-400 font-bold mb-2">{t("archive")}</div>
          <h1 className="font-black text-4xl md:text-5xl tracking-tight">{t("history_title")}</h1>
        </div>
        {hasPlan && (
          <Button onClick={() => setShowManual(true)} data-testid="add-manual-btn" className="rounded-full bg-orange-500 hover:bg-orange-600 text-black font-black h-10 px-5">
            <Plus className="w-4 h-4 mr-1.5" /> {t("add_manual_prompt") || "Add prompt"}
          </Button>
        )}
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        {[
          { k: "all", label: t("all") },
          { k: "favorites", label: t("favorites") },
          { k: "works", label: t("works") || "Works" },
          { k: "needs_fix", label: t("needs_fix") || "Needs fix" },
        ].map((f) => (
          <button key={f.k} onClick={() => setTab(f.k)} data-testid={`tab-${f.k}`}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors duration-200 ${tab === f.k ? "bg-orange-500 text-black" : "bg-white/5 text-zinc-400 hover:text-white border border-white/10"}`}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Search bar (searches title + prompt text server-side, debounced) */}
      <div className="relative mb-6 max-w-xl">
        <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <Input
          data-testid="library-search-input"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={t("search_prompts_ph") || "Search by title or prompt text…"}
          maxLength={200}
          className="pl-10 pr-9 bg-[#0E0E12]/40 border-white/10 h-10 text-sm"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => setSearchQuery("")}
            data-testid="library-search-clear"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white p-1"
            title={t("clear") || "Clear"}
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Lists row (Plus/Pro perk) */}
      {hasPlan && (
        <div className="flex gap-2 items-center flex-wrap mb-6 pb-4 border-b border-white/5">
          <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mr-1">{t("my_lists") || "Lists"}</span>
          <button onClick={() => setActiveListId("")} data-testid="list-all" className={`px-3 py-1 rounded-full text-xs font-semibold ${activeListId === "" ? "bg-orange-500/20 text-orange-300 border border-orange-500/30" : "bg-white/5 text-zinc-400 hover:text-white border border-white/10"}`}>
            {t("all_prompts") || "All"}
          </button>
          {lists.map((L) => (
            <div key={L.list_id} className="inline-flex items-center gap-1">
              <button onClick={() => setActiveListId(L.list_id)} data-testid={`list-${L.list_id}`}
                className={`px-3 py-1 rounded-full text-xs font-semibold ${activeListId === L.list_id ? "bg-orange-500/20 text-orange-300 border border-orange-500/30" : "bg-white/5 text-zinc-400 hover:text-white border border-white/10"}`}>
                <FolderOpen className="w-3 h-3 inline mr-1" />{L.name} <span className="opacity-60">· {L.prompt_ids?.length || 0}</span>
              </button>
              <button onClick={() => deleteList(L.list_id)} data-testid={`del-list-${L.list_id}`} className="text-zinc-600 hover:text-red-400 p-1" title={t("delete_list") || "Delete list"}>
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          <button onClick={() => setShowNewList(true)} data-testid="new-list-btn" className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold text-orange-400 hover:text-orange-300 border border-dashed border-orange-500/40 hover:bg-orange-500/[0.06]">
            <FolderPlus className="w-3 h-3" /> {t("new_list") || "New list"}
          </button>
        </div>
      )}

      {isFreeTier && (
        <div className="mb-6 rounded-md border border-orange-500/30 bg-orange-500/[0.06] p-4 flex items-center gap-4 flex-wrap" data-testid="free-tier-usage">
          <div className="flex-1 min-w-[220px]">
            <div className="text-[10px] uppercase tracking-widest text-orange-400 font-bold mb-1">{t("free_trial") || "Free trial"}</div>
            <div className="text-sm text-zinc-200 leading-relaxed">
              <span data-testid="usage-manual-count"><strong>{usage.manual_used}</strong> / {usage.manual_limit === -1 ? "∞" : usage.manual_limit}</span> {t("manual_prompts") || "manual prompts"}
              {" · "}
              <span data-testid="usage-lists-count"><strong>{usage.lists_used}</strong> / {usage.lists_limit === -1 ? "∞" : usage.lists_limit}</span> {t("custom_lists") || "custom lists"}
              {(user?.signup_bonus_remaining ?? 0) > 0 && (
                <>{" · "}<span data-testid="usage-signup-bonus"><strong>{user.signup_bonus_remaining}</strong> {t("free_ai_prompts_left") || "credits left"}</span></>
              )}
            </div>
          </div>
          <Link to="/app/account"><Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-black font-bold rounded-full" data-testid="free-tier-upgrade-btn">{t("upgrade_now") || "Upgrade"}</Button></Link>
        </div>
      )}

      {items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-white/10 bg-[#121218] p-12 text-center" data-testid="history-empty">
          <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-orange-500/10 border border-orange-500/25 flex items-center justify-center">
            <Sparkles className="w-9 h-9 text-orange-400" strokeWidth={1.6} />
          </div>
          <div className="text-zinc-400 text-sm">{tab === "favorites" ? t("no_favorites") : t("no_prompts")}.</div>
        </div>
      ) : (
        <>
          {hasPlan && (
            <div className="flex items-center gap-3 mb-3 text-xs text-zinc-500" data-testid="bulk-controls">
              <button onClick={selectAll} data-testid="bulk-select-all" className="hover:text-white">{t("select_all") || "Select all"}</button>
              <span>·</span>
              <button onClick={clearSelection} data-testid="bulk-clear" className="hover:text-white" disabled={selected.size === 0}>{t("clear_selection") || "Clear"}</button>
              <span className="text-zinc-600">{selected.size > 0 && `· ${selected.size} ${t("selected") || "selected"}`}</span>
            </div>
          )}
          <div className="space-y-2">
            {items.map((p) => {
              const checked = selected.has(p.prompt_id);
              const status = STATUS_UI[p.status || "works"] || STATUS_UI.works;
              const isEditingTitle = editingTitleId === p.prompt_id;
              return (
                <div key={p.prompt_id} className={`rounded-md border p-4 md:p-5 transition-colors duration-200 ${checked ? "border-orange-500/50 bg-orange-500/[0.04]" : "border-white/10 bg-white/[0.02] hover:border-white/20"}`} data-testid={`history-item-${p.prompt_id}`}>
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 md:gap-4 mb-3">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      {hasPlan && (
                        <Checkbox checked={checked} onCheckedChange={() => toggleSelect(p.prompt_id)} data-testid={`select-${p.prompt_id}`}
                          className="mt-1 border-white/20 data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500" />
                      )}
                      {p.has_thumbnail && (
                        <button
                          type="button"
                          onClick={() => setPreviewPrompt(p)}
                          data-testid={`thumb-open-${p.prompt_id}`}
                          className="shrink-0 focus:outline-none focus:ring-2 focus:ring-orange-500/60 rounded"
                          title={t("preview_prompt") || "Preview"}
                        >
                          <PromptThumb promptId={p.prompt_id} version={p.thumbnail_updated_at || ""} size={56} />
                        </button>
                      )}
                      <div className="min-w-0 flex-1">
                        {/* Title (inline editable) */}
                        {isEditingTitle ? (
                          <input autoFocus value={titleDraft}
                            onChange={(e) => setTitleDraft(e.target.value)}
                            onBlur={() => saveTitle(p)}
                            onKeyDown={(e) => { if (e.key === "Enter") saveTitle(p); if (e.key === "Escape") setEditingTitleId(null); }}
                            data-testid={`title-input-${p.prompt_id}`} maxLength={120}
                            className="bg-transparent border-b border-orange-500/50 outline-none text-white font-bold text-lg w-full" />
                        ) : (
                          <button onClick={() => hasPlan && beginEditTitle(p)} data-testid={`title-btn-${p.prompt_id}`}
                            className={`font-bold text-lg text-left truncate w-full ${p.title ? "text-white" : "text-zinc-500 italic"} ${hasPlan ? "hover:text-orange-400" : ""}`}
                            title={hasPlan ? (t("edit_title") || "Click to rename") : ""}>
                            {p.title || (t("untitled_prompt") || "Untitled prompt")}
                          </button>
                        )}
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <button onClick={() => hasPlan && cycleStatus(p)} data-testid={`status-${p.prompt_id}`}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-bold ${status.bg} ${status.color} ${hasPlan ? "hover:opacity-80" : ""}`}
                            title={hasPlan ? (t("toggle_status") || "Click to toggle status") : ""}>
                            <status.Icon className="w-3 h-3" />{t(status.label) || status.label}
                          </button>
                          <span className="text-[10px] uppercase tracking-widest text-orange-400 font-bold">{p.model_name} · {p.output_type}</span>
                          {p.source === "manual" && <span className="text-[10px] uppercase tracking-widest text-purple-300 font-bold bg-purple-500/10 px-2 py-0.5 rounded-full">{t("source_manual") || "manual"}</span>}
                          {p.has_references && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-sky-300 font-bold bg-sky-500/10 border border-sky-500/20 px-2 py-0.5 rounded-full"
                              data-testid={`ref-badge-${p.prompt_id}`}
                              title={t("ref_used_tooltip") || `Generated with ${p.reference_count} reference image(s)`}
                            >
                              REF{p.reference_count > 1 ? ` ×${p.reference_count}` : ""}
                            </span>
                          )}
                          {p.last_generation && (p.last_generation.storage_mode === "vixelforge" && p.last_generation.has_media ? (<>
                            <button type="button" onClick={() => navigate(`/app/gallery?open=${p.last_generation.generation_id}`)}
                              data-testid={`media-link-${p.prompt_id}`}
                              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-emerald-300 bg-emerald-500/10 border border-emerald-500/25 px-2 py-0.5 rounded-full hover:bg-emerald-500/20 transition-colors">
                              {p.last_generation.type === "video" ? <Film className="w-3 h-3" /> : <ImageIcon className="w-3 h-3" />}
                              {p.last_generation.type === "video" ? "VIDEO" : "IMG"} · VIXELFORGE
                            </button>
                            <button type="button" onClick={() => navigate(`/app/studio?gen=${p.last_generation.generation_id}`)}
                              data-testid={`open-in-studio-${p.prompt_id}`} title={t("open_in_studio")}
                              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-orange-300 bg-orange-500/10 border border-orange-500/25 px-2 py-0.5 rounded-full hover:bg-orange-500/20 transition-colors">
                              <Wand2 className="w-3 h-3" /> {t("open_in_studio")}
                            </button>
                          </>
                          ) : (p.last_generation.storage_mode === "local" || p.last_generation.storage_mode === "drive") ? (
                            <span data-testid={`media-dest-${p.prompt_id}`}
                              title={p.last_generation.storage_mode === "drive" ? "Google Drive" : "Local folder"}
                              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-amber-200 bg-amber-500/10 border border-amber-500/25 px-2 py-0.5 rounded-full">
                              {p.last_generation.storage_mode === "drive" ? <Cloud className="w-3 h-3" /> : <FolderOpen className="w-3 h-3" />}
                              {p.last_generation.storage_mode === "drive" ? "DRIVE" : "FOLDER"}
                            </span>
                          ) : null)}
                          {(p.list_names && p.list_names.length > 0) && (
                            <div className="relative inline-flex items-center">
                              <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-bold bg-orange-500/10 text-orange-300 border border-orange-500/20"
                                data-testid={`list-badge-${p.prompt_id}`}
                                title={p.list_names.join(", ")}
                              >
                                <FolderOpen className="w-3 h-3" />
                                <span className="max-w-[140px] truncate normal-case tracking-normal">{p.list_names[0]}</span>
                                {p.list_names.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); setListNamesPopover(listNamesPopover === p.prompt_id ? null : p.prompt_id); }}
                                    data-testid={`list-badge-more-${p.prompt_id}`}
                                    className="ml-0.5 rounded-full bg-orange-500/20 px-1.5 py-[1px] text-[9px] hover:bg-orange-500/40"
                                  >
                                    +{p.list_names.length - 1}
                                  </button>
                                )}
                              </span>
                              {listNamesPopover === p.prompt_id && p.list_names.length > 1 && (
                                <div
                                  className="absolute left-0 top-full mt-1 z-20 bg-[#121218] border border-white/10 rounded-md py-2 min-w-[180px] max-w-[260px] shadow-lg"
                                  data-testid={`list-names-popover-${p.prompt_id}`}
                                  onMouseLeave={() => setListNamesPopover(null)}
                                >
                                  <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold px-3 pb-1">{t("also_in") || "Also in"}</div>
                                  {p.list_names.slice(1).map((n) => (
                                    <div key={n} className="px-3 py-1 text-xs text-zinc-300 flex items-center gap-2">
                                      <FolderOpen className="w-3 h-3 text-orange-400/70 shrink-0" />
                                      <span className="truncate">{n}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                          <span className="text-xs text-zinc-500">{new Date(p.created_at).toLocaleString(lang)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1 relative flex-wrap justify-end md:justify-start md:flex-nowrap shrink-0">
                      {hasPlan && lists.length > 0 && (
                        <div ref={addToListMenu === p.prompt_id ? addToListMenuRef : null} className="relative inline-flex">
                          <Button size="sm" variant="ghost" onClick={() => setAddToListMenu(addToListMenu === p.prompt_id ? null : p.prompt_id)} data-testid={`add-to-list-${p.prompt_id}`} className="text-zinc-400 hover:text-orange-400" title={t("manage_lists") || "Manage lists"}>
                            <ListPlus className="w-4 h-4" />
                          </Button>
                          {addToListMenu === p.prompt_id && (
                            <div className="absolute right-0 top-full mt-1 z-10 bg-[#121218] border border-white/10 rounded-md py-2 min-w-[220px] shadow-lg" data-testid={`manage-lists-menu-${p.prompt_id}`}>
                              <div className="px-3 pb-2 text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{t("manage_lists") || "Manage lists"}</div>
                              {lists.length === 0 ? (
                                <div className="px-3 py-1.5 text-xs text-zinc-500 italic">{t("no_lists_yet") || "No lists yet"}</div>
                              ) : lists.map((L) => {
                                const inList = !!L.prompt_ids?.includes(p.prompt_id);
                                return (
                                  <button key={L.list_id} onClick={() => togglePromptInList(p.prompt_id, L.list_id)}
                                    data-testid={`toggle-${p.prompt_id}-in-${L.list_id}`}
                                    className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 ${inList ? "text-orange-300 hover:bg-orange-500/10" : "text-zinc-300 hover:bg-white/5 hover:text-white"}`}>
                                    <span className={`w-4 h-4 rounded-sm border flex items-center justify-center shrink-0 ${inList ? "border-orange-400 bg-orange-500/20" : "border-white/20"}`}>
                                      {inList && <Check className="w-3 h-3 text-orange-300" />}
                                    </span>
                                    <FolderOpen className={`w-3 h-3 opacity-70 shrink-0 ${inList ? "text-orange-300" : ""}`} />
                                    <span className="truncate">{L.name}</span>
                                  </button>
                                );
                              })}
                              <div className="border-t border-white/10 mt-2 pt-2 px-2">
                                <Button
                                  size="sm"
                                  onClick={() => setAddToListMenu(null)}
                                  data-testid={`manage-lists-done-${p.prompt_id}`}
                                  className="w-full h-8 bg-orange-500 hover:bg-orange-600 text-black font-bold text-xs uppercase tracking-widest"
                                >
                                  <Check className="w-3.5 h-3.5 mr-1.5" /> {t("done") || "Done"}
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      {p.source !== "manual" && (
                        <Button size="sm" variant="ghost" onClick={() => navigate(`/app?edit=${p.prompt_id}${p.last_generation?.has_media ? `&media=${p.last_generation.generation_id}` : ''}`)} data-testid={`edit-${p.prompt_id}`} className="text-zinc-400 hover:text-orange-400" title={t("edit_prompt")}><Pencil className="w-4 h-4" /></Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => setPreviewPrompt(p)} data-testid={`preview-${p.prompt_id}`} className="text-zinc-400 hover:text-orange-400" title={t("preview_prompt") || "Preview full prompt"}><Eye className="w-4 h-4" /></Button>
                      {canUploadThumb && (
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={thumbUploadingFor === p.prompt_id}
                          onClick={() => openThumbPicker(p.prompt_id)}
                          data-testid={`thumb-upload-${p.prompt_id}`}
                          className="text-zinc-400 hover:text-orange-400"
                          title={p.has_thumbnail ? (t("thumb_replace") || "Replace image") : (t("thumb_add") || "Add reference image")}
                        >
                          {p.has_thumbnail ? <ImageIcon className="w-4 h-4" /> : <ImagePlus className="w-4 h-4" />}
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => copy(p.generated_prompt)} data-testid={`copy-${p.prompt_id}`} className="text-zinc-400 hover:text-white"><Copy className="w-4 h-4" /></Button>
                      {canExportPdf && (
                        <Button size="sm" variant="ghost" onClick={() => singleExport(p)} data-testid={`export-pdf-${p.prompt_id}`} className="text-zinc-400 hover:text-orange-400" title={t("download_pdf") || "Download PDF"}>
                          <FileDown className="w-4 h-4" />
                        </Button>
                      )}
                      {hasPlan && (
                        <Button size="sm" variant="ghost" onClick={() => toggleFav(p.prompt_id)} data-testid={`fav-${p.prompt_id}`} className={p.is_favorite ? "text-orange-400" : "text-zinc-400 hover:text-white"}>
                          <Heart className={`w-4 h-4 ${p.is_favorite ? "fill-orange-400" : ""}`} />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => del(p.prompt_id)} data-testid={`del-${p.prompt_id}`} className="text-zinc-400 hover:text-red-400"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                  <p className="text-sm text-zinc-300 font-mono line-clamp-3">{p.generated_prompt}</p>
                </div>
              );
            })}
          </div>
        </>
      )}

      {hasPlan && selected.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 rounded-full border border-white/10 bg-[#121218] shadow-[0_8px_32px_rgba(0,0,0,0.6)] px-4 py-2 flex items-center gap-2" data-testid="bulk-toolbar">
          <span className="text-xs text-zinc-400 px-2" data-testid="bulk-count">{selected.size} {t("selected") || "selected"}</span>

          <div className="relative">
            <Button size="sm" variant="outline" disabled={bulkBusy} onClick={() => setBulkAddMenu((v) => !v)} data-testid="bulk-add-to-list-btn" className="rounded-full bg-white/5 border-white/10 hover:bg-white/10 text-white text-xs h-8">
              <ListPlus className="w-3.5 h-3.5 mr-1.5" /> {t("bulk_add_to_list") || "Add to list"}
            </Button>
            {bulkAddMenu && (
              <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 w-64 rounded-xl border border-white/10 bg-[#121218] shadow-[0_8px_32px_rgba(0,0,0,0.6)] p-2 z-50" data-testid="bulk-add-menu">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold px-2 py-1">{t("choose_list") || "Choose a list"}</div>
                <div className="max-h-48 overflow-y-auto">
                  {lists.length === 0 && (
                    <div className="text-xs text-zinc-500 px-2 py-2">{t("no_lists_yet") || "No lists yet"}</div>
                  )}
                  {lists.map((L) => (
                    <button key={L.list_id} onClick={() => bulkAddToList(L.list_id)} disabled={bulkBusy}
                      data-testid={`bulk-add-to-${L.list_id}`}
                      className="w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-zinc-200 hover:bg-white/5 disabled:opacity-50">
                      <FolderOpen className="w-3.5 h-3.5 text-orange-400 shrink-0" />
                      <span className="truncate">{L.name}</span>
                    </button>
                  ))}
                </div>
                <div className="border-t border-white/10 mt-2 pt-2">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold px-2 py-1">{t("or_create_new") || "Or create new"}</div>
                  <div className="flex items-center gap-1 px-1">
                    <Input value={newListName} onChange={(e) => setNewListName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") bulkCreateAndAdd(); }}
                      maxLength={80} placeholder={t("list_name_ph") || "New list name…"}
                      data-testid="bulk-new-list-input"
                      className="bg-[#0E0E12]/40 border-white/10 h-8 text-xs" />
                    <Button size="sm" onClick={bulkCreateAndAdd} disabled={bulkBusy || !newListName.trim()}
                      data-testid="bulk-new-list-create"
                      className="bg-orange-500 hover:bg-orange-600 text-black font-bold h-8 px-2">
                      <Plus className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {canExportPdf && (
            <Button size="sm" variant="outline" disabled={bulkBusy} onClick={bulkExport} data-testid="bulk-export-btn" className="rounded-full bg-white/5 border-white/10 hover:bg-white/10 text-white text-xs h-8">
              <FileDown className="w-3.5 h-3.5 mr-1.5" /> {t("bulk_export_pdf") || "Export PDF"}
            </Button>
          )}
          {canExportPdf && (
            <Button size="sm" disabled={bulkBusy} onClick={bulkZip} data-testid="bulk-zip-btn" className="rounded-full bg-orange-500 hover:bg-orange-400 text-black font-bold text-xs h-8">
              {bulkBusy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />} {{ it: "Scarica ZIP", es: "Descargar ZIP", fr: "Télécharger ZIP", de: "ZIP herunterladen" }[lang] || "Download ZIP"}
            </Button>
          )}
          <Button size="sm" disabled={bulkBusy} onClick={bulkDelete} data-testid="bulk-delete-btn" className="rounded-full bg-red-500 hover:bg-red-600 text-white text-xs h-8">
            <Trash2 className="w-3.5 h-3.5 mr-1.5" /> {t("bulk_delete") || "Delete"}
          </Button>
          <button onClick={clearSelection} className="text-zinc-500 hover:text-white ml-1" data-testid="bulk-toolbar-close"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* New List modal */}
      <Dialog open={showNewList} onOpenChange={setShowNewList}>
        <DialogContent className="bg-[#121218] border-white/10 text-white" data-testid="new-list-dialog">
          <DialogHeader><DialogTitle>{t("new_list") || "New list"}</DialogTitle></DialogHeader>
          <Input autoFocus data-testid="new-list-input" value={newListName} onChange={(e) => setNewListName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") createList(); }} maxLength={80} placeholder={t("list_name_ph") || "e.g. Portraits, Landscapes, Reels…"}
            className="bg-[#0E0E12]/40 border-white/10" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewList(false)} className="bg-white/5 border-white/10">{t("cancel") || "Cancel"}</Button>
            <Button onClick={createList} data-testid="new-list-save" className="bg-orange-500 hover:bg-orange-600 text-black font-bold">{t("create") || "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual prompt modal */}
      <Dialog open={showManual} onOpenChange={setShowManual}>
        <DialogContent className="bg-[#121218] border-white/10 text-white max-w-2xl" data-testid="manual-dialog">
          <DialogHeader><DialogTitle>{t("add_manual_prompt") || "Add prompt manually"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5 block">{t("prompt_title") || "Title"}</label>
              <Input data-testid="manual-title" value={manualForm.title} onChange={(e) => setManualForm({...manualForm, title: e.target.value})} maxLength={120} placeholder={t("prompt_title_ph") || "e.g. Golden hour portrait"} className="bg-[#0E0E12]/40 border-white/10" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5 block">{t("source_model") || "Source model (optional)"}</label>
                <Input data-testid="manual-model" value={manualForm.model_name} onChange={(e) => setManualForm({...manualForm, model_name: e.target.value})} maxLength={80} placeholder="Midjourney, Sora, Kling…" className="bg-[#0E0E12]/40 border-white/10" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5 block">{t("output_type") || "Output"}</label>
                <Select value={manualForm.output_type} onValueChange={(v) => setManualForm({...manualForm, output_type: v})}>
                  <SelectTrigger data-testid="manual-output" className="bg-[#0E0E12]/40 border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#111] border-white/10">
                    <SelectItem value="image">{t("image") || "Image"}</SelectItem>
                    <SelectItem value="video">{t("video") || "Video"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5 block">{t("prompt_text") || "Prompt text"} *</label>
              <Textarea data-testid="manual-text" rows={8} value={manualForm.generated_prompt} onChange={(e) => setManualForm({...manualForm, generated_prompt: e.target.value})} maxLength={8000} placeholder={t("manual_text_ph") || "Paste or type your prompt here…"} className="bg-[#0E0E12]/40 border-white/10 font-mono text-sm" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowManual(false)} className="bg-white/5 border-white/10">{t("cancel") || "Cancel"}</Button>
            <Button onClick={submitManual} data-testid="manual-save" className="bg-orange-500 hover:bg-orange-600 text-black font-bold">{t("save") || "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cap-reached upgrade modal */}
      <Dialog open={!!capModal} onOpenChange={(o) => !o && setCapModal(null)}>
        <DialogContent className="bg-[#121218] border-white/10 text-white max-w-md" data-testid="cap-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-orange-400" />
              {capModal?.type === "manual"
                ? (t("cap_manual_title") || "Manual prompts cap reached")
                : (t("cap_list_title") || "Custom lists cap reached")}
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-zinc-300 leading-relaxed">
            {capModal?.type === "manual"
              ? (t("cap_manual_body") || `You've used all ${capModal?.limit} manual slots on your current plan. Upgrade to store more prompts from your favorite tools.`).replace("{limit}", capModal?.limit)
              : (t("cap_list_body") || `You've reached the ${capModal?.limit}-list cap on your current plan. Upgrade to create more curated collections.`).replace("{limit}", capModal?.limit)
            }
          </div>
          <div className="rounded-md border border-white/10 bg-white/[0.02] p-4 text-xs text-zinc-400 space-y-1.5">
            <div className="text-[10px] uppercase tracking-widest text-orange-400 font-bold mb-1">{t("what_you_get") || "What you get by upgrading"}</div>
            <div>· <strong className="text-zinc-200">Basic</strong> — 100 {t("manual_prompts") || "manual prompts"} · 5 {t("custom_lists") || "custom lists"}</div>
            <div>· <strong className="text-zinc-200">Plus</strong> — 500 {t("manual_prompts") || "manual prompts"} · 20 {t("custom_lists") || "custom lists"}</div>
            <div>· <strong className="text-zinc-200">Pro</strong> — ∞ {t("manual_prompts") || "manual prompts"} · ∞ {t("custom_lists") || "custom lists"}</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCapModal(null)} data-testid="cap-later-btn" className="bg-white/5 border-white/10">
              {t("maybe_later") || "Maybe later"}
            </Button>
            <Link to="/app/account">
              <Button data-testid="cap-upgrade-btn" onClick={() => setCapModal(null)} className="bg-orange-500 hover:bg-orange-600 text-black font-bold">
                {t("see_plans") || "See plans"}
              </Button>
            </Link>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Full prompt preview */}
      <Dialog open={!!previewPrompt} onOpenChange={(o) => !o && setPreviewPrompt(null)}>
        <DialogContent className="bg-[#121218] border-white/10 max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="preview-dialog">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">
              {previewPrompt?.title || (t("preview_prompt") || "Full prompt")}
            </DialogTitle>
            {previewPrompt && (
              <div className="text-[10px] uppercase tracking-widest text-orange-400 font-bold mt-1">
                {previewPrompt.model_name} · {previewPrompt.output_type}
                <span className="text-zinc-500 ml-2 normal-case tracking-normal font-normal">
                  {new Date(previewPrompt.created_at).toLocaleString(lang)}
                </span>
              </div>
            )}
          </DialogHeader>
          {previewPrompt?.has_thumbnail && (
            <div className="mt-3 flex justify-center bg-[#0E0E12]/60 border border-white/5 rounded p-3">
              <PromptThumb
                promptId={previewPrompt.prompt_id}
                version={previewPrompt.thumbnail_updated_at || ""}
                size={280}
                className="!w-auto !h-auto max-h-[200px] sm:max-h-[280px] max-w-full"
              />
            </div>
          )}
          <div className="mt-3 max-h-[35vh] sm:max-h-[40vh] overflow-y-auto bg-[#0E0E12]/40 border border-white/5 rounded p-4">
            <pre className="whitespace-pre-wrap break-words text-sm text-zinc-100 font-mono leading-relaxed" data-testid="preview-body">
              {previewPrompt?.generated_prompt || ""}
            </pre>
          </div>
          <DialogFooter className="mt-4 gap-2 flex-col sm:flex-row sm:justify-between">
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              {canUploadThumb && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => previewPrompt && openThumbPicker(previewPrompt.prompt_id)}
                    disabled={thumbUploadingFor === previewPrompt?.prompt_id}
                    data-testid="preview-thumb-upload"
                    className="bg-white/5 border-white/10 hover:bg-white/10 text-white"
                  >
                    {previewPrompt?.has_thumbnail
                      ? (<><ImageIcon className="w-4 h-4 mr-2" /> {t("thumb_replace") || "Replace image"}</>)
                      : (<><ImagePlus className="w-4 h-4 mr-2" /> {t("thumb_add") || "Add image"}</>)}
                  </Button>
                  {previewPrompt?.has_thumbnail && (
                    <Button
                      variant="outline"
                      onClick={() => deleteThumb(previewPrompt.prompt_id)}
                      data-testid="preview-thumb-delete"
                      className="bg-white/5 border-white/10 hover:bg-red-500/10 text-red-300"
                    >
                      <Trash2 className="w-4 h-4 mr-2" /> {t("thumb_remove") || "Remove image"}
                    </Button>
                  )}
                </>
              )}
            </div>
            <div className="flex gap-2 w-full sm:w-auto">
              <Button
                variant="outline"
                onClick={() => previewPrompt && copy(previewPrompt.generated_prompt)}
                data-testid="preview-copy-btn"
                className="bg-white/5 border-white/10 hover:bg-white/10 text-white"
              >
                <Copy className="w-4 h-4 mr-2" /> {t("copy") || "Copy"}
              </Button>
              <Button
                onClick={() => setPreviewPrompt(null)}
                data-testid="preview-close-btn"
                className="bg-orange-500 hover:bg-orange-600 text-black font-bold"
              >
                {t("close") || "Close"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
